import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./AdministratorSidebar.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaSearch } from "react-icons/fa";
import logo from "./assets/logo.jpg";

const MemberSidebar = () => {
  const location = useLocation(); // ← needed to know current page
  const [isSidebarActive, setIsSidebarActive] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [userFullName, setUserFullName] = useState("");
  const [userType, setUserType] = useState("");
  const [userImage, setUserImage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const getDefaultAvatar = (name) => {
    if (!name) return "https://ui-avatars.com/api/?name=User&background=F6CA13&color=2B363C&size=80";
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=F6CA13&color=2B363C&size=80`;
  };

  const matchesSearch = (text) =>
    text.toLowerCase().includes(searchTerm.toLowerCase());

  const toggleSidebar = () => {
    setIsSidebarActive(!isSidebarActive);
  };

  // Toggle only when user clicks the parent menu
  const toggleSubmenu = (submenu) => {
    if (window.innerWidth <= 768) {
      setIsSidebarActive(true);
    }
    setOpenSubmenu((prev) => (prev === submenu ? null : submenu));
  };

  // ========== AUTO-OPEN SUBMENU BASED ON CURRENT ROUTE ==========
  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith("/vehicles") || path.startsWith("/add-vehicle")) {
      setOpenSubmenu("vehicles");
    } else if (path.startsWith("/view-sales") || path.startsWith("/add-new-sale")) {
      setOpenSubmenu("sales");
    } else if (path.startsWith("/documents-shipping")) {
      setOpenSubmenu("docs");
    } else if (
      path.startsWith("/volet") ||
      path.startsWith("/create-volet") ||
      path.startsWith("/view-payments") ||
      path.startsWith("/create-allocate")
    ) {
      setOpenSubmenu("payments");
    } else if (
      path.startsWith("/view-user") ||
      path.startsWith("/create-account") ||
      path.startsWith("/view-groups") ||
      path.startsWith("/create-group")
    ) {
      setOpenSubmenu("settings");
    }
    // You can add more conditions if needed
  }, [location.pathname]);
  // ==============================================================

  const fetchUserProfile = async () => {
    const userId = localStorage.getItem("userId") || localStorage.getItem("user_id");

    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("https://skcapi.btcjapan.co/get_user_profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: userId }),
      });
      const data = await response.json();

      if (data.Status === "200" && data.User) {
        let fullName = "User";
        if (data.User.fname && data.User.lname) {
          fullName = `${data.User.fname} ${data.User.lname}`;
          localStorage.setItem("fname", data.User.fname);
          localStorage.setItem("lname", data.User.lname);
        } else if (data.User.fname) {
          fullName = data.User.fname;
          localStorage.setItem("fname", data.User.fname);
        }
        setUserFullName(fullName);

        if (data.User.usertype) {
          setUserType(data.User.usertype);
          localStorage.setItem("usertype", data.User.usertype);
        }

        if (data.User.image_url) {
          setUserImage(data.User.image_url);
        } else {
          setUserImage(getDefaultAvatar(fullName));
        }
      } else {
        const storedFName = localStorage.getItem("fname") || "";
        const storedLName = localStorage.getItem("lname") || "";
        const fullName = `${storedFName} ${storedLName}`.trim() || "User";
        setUserFullName(fullName);
        setUserImage(getDefaultAvatar(fullName));
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedFName = localStorage.getItem("fname") || "";
    const storedLName = localStorage.getItem("lname") || "";
    const fullName = `${storedFName} ${storedLName}`.trim() || "User";
    setUserFullName(fullName);
    setUserImage(getDefaultAvatar(fullName));
    fetchUserProfile();
  }, []);

  return (
    <div className="wrapper">
      {/* Sidebar Toggle Button for Mobile */}
      <button
        type="button"
        id="sidebarCollapse"
        className="btn btn-info d-md-none"
        onClick={toggleSidebar}
      >
        <i className="fas fa-bars"></i>
      </button>

      {/* Sidebar */}
      <nav id="sidebar" className={isSidebarActive ? "active" : ""}>
        <div className="sidebar-header">
          <div className="admin-brand">
            <img src={logo} alt="Logo" className="logo" />
          </div>
          
          <div className="user-brand">
            {!loading && (
              <img 
                src={userImage || getDefaultAvatar(userFullName)} 
                alt={userFullName || "User"} 
                className="userimg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = getDefaultAvatar(userFullName || "User");
                }}
              />
            )}
          </div>
          
          <p className="user-name">{userFullName || "User"}</p>

          <div className="searchbar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search in menu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <ul className="list-unstyled components">
          {/* Dashboard */}
          {matchesSearch("Dashboard") && (
            <li>
              <NavLink
                to="/admin-panel"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-tachometer-alt"></i>
                Dashboard
              </NavLink>
            </li>
          )}

          {/* Leads */}
          {matchesSearch("Leads") && (
            <li>
              <NavLink
                to="/view-leads"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-gavel"></i>
                Leads
              </NavLink>
            </li>
          )}

          {/* Client */}
          {matchesSearch("Client") && (
            <li>
              <NavLink
                to="/view-client"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-gavel"></i>
                Client
              </NavLink>
            </li>
          )}

          {/* Bids */}
          {matchesSearch("Bid") && (
            <li>
              <NavLink
                to="/view-bids"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-gavel"></i>
                Bids
              </NavLink>
            </li>
          )}

          {/* Stock */}
          {matchesSearch("Stock") && (
            <li>
              <NavLink
                to="/view-stock"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-gavel"></i>
                Stock
              </NavLink>
            </li>
          )}

          {/* Sale Order */}
          {matchesSearch("SaleOrder") && (
            <li>
              <NavLink
                to="/view-sale-order"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-gavel"></i>
                Sale Order
              </NavLink>
            </li>
          )}                 

          {/* Profile */}
          {matchesSearch("Profile") && (
            <li>
              <NavLink
                to="/portfolio"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-users"></i>
                Profile
              </NavLink>
            </li>
          )}
        </ul>
      </nav>

      {/* Overlay for mobile when sidebar is active */}
      {isSidebarActive && <div className="overlay" onClick={toggleSidebar}></div>}
    </div>
  );
};

export default MemberSidebar;